/**
 * Générateur — Automatisme : diviser un décimal par 1, 10, 100 ou 1 000
 * Attendu ID : 1009 | Niveau : 6ème | Domaine : NC
 * Rubrique : AUTOMATISME (programme 2025) — restitution en rituel court.
 * Labels (cartes exerciseur) :
 *   - Diviser par 1, 10, 100 ou 1 000
 *
 * ── Plan de cartes (2026-08-20, miroir du gen_1008) ────────────────────────
 * UNE carte, 5 formats rituels. LE NOMBRE GLISSE VERS LA DROITE, LA VIRGULE
 * RESTE FIXE. Calcul par GLISSEMENT DE CHAÎNE puis RE-VÉRIFIÉ numériquement.
 * Opérande a : ENTIER MAJORITAIRE (1 à 4 chiffres, zéros finaux ADMIS — la
 * flash a 850 et 1 250 : c'est le piège du « zéro avalé », 850 ÷ 1 000 =
 * 0,85) ou décimal E,D (E ∈ [0..12], D canonique). Diviseur m ∈ {1, 10,
 * 100, 1000}. INVARIANTS :
 *   - RÉSULTAT ≤ 3 DÉCIMALES (comme la flash : 0,072, jamais 0,00425) —
 *     pour un décimal, q est contraint par dec(a) + q ≤ 3 ;
 *   - résultat toujours CANONIQUE (les zéros finaux du quotient sont
 *     avalés : 850 ÷ 1 000 = 0,85, pas « 0,850 ») ;
 *   - chaque tirage multi-items : EXACTEMENT UN « ÷ 1 » (anti-routine),
 *     au moins 2 entiers (ils dominent la flash), au moins un décimal,
 *     au moins un résultat < 1 (la traversée descendante : 47 ÷ 100) ;
 *   - saisie : trou sur le RÉSULTAT, ou parfois sur le DIVISEUR
 *     (« 3,2 ÷ {{100}} = 0,032 », unique par construction) — JAMAIS sur le
 *     dividende (ce serait une multiplication : gen_1008, frontière A5) ;
 *   - QCM : distracteurs NOMMÉS (sens_inverse « 47 ÷ 10 = 470 » : LA
 *     confusion multiplier/diviser ; glissements ±1 ; routine_appliquee
 *     pour ÷ 1), distincts de la clé en texte ET en valeur ;
 *   - V/F : lignes fausses re-vérifiées FAUSSES numériquement ;
 *   - affichage : milliers espacés (1 250), mots de trous en brut.
 */
import {
    randomChoice, randInt, shuffle, formatNumber,
    makeQcm, makeVraiFaux, makeTexteTrou, makeClasser, filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 1009;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Diviser par 1, 10, 100 ou 1 000';

// ─── Nombres en chaînes (le glissement est un décalage de rang) ────────────

/** Un nombre = { ent: '850', dec: '' } (dec vide pour un entier). */
const valeurDe = (n) => Number(`${n.ent}.${n.dec || '0'}`);
const brut = (n) => (n.dec ? `${n.ent},${n.dec}` : n.ent);
const affiche = (n) => (n.dec
    ? `${formatNumber(Number(n.ent))},${n.dec}`
    : formatNumber(Number(n.ent)));

/** Glissement de k rangs (k > 0 : gauche/×, k < 0 : droite/÷), virgule fixe. */
const glisser = (n, k) => {
    let digits = n.ent + n.dec;
    const newDec = n.dec.length - k;
    if (newDec <= 0) {
        const ent = (digits + '0'.repeat(-newDec)).replace(/^0+(?=\d)/, '');
        return { ent, dec: '' };
    }
    while (digits.length <= newDec) digits = `0${digits}`;
    return {
        ent: digits.slice(0, -newDec).replace(/^0+(?=\d)/, ''),
        dec: digits.slice(-newDec),
    };
};

/** Canonise : avale les zéros finaux de la partie décimale (0,850 → 0,85). */
const canon = (n) => {
    let dec = n.dec;
    while (dec.endsWith('0')) dec = dec.slice(0, -1);
    return { ent: n.ent, dec };
};

/** Le quotient a ÷ 10^q, canonique. */
const quotientDe = (a, q) => canon(glisser(a, -q));

/** AUTO-VÉRIFICATION : glissement et arithmétique doivent coïncider. */
const verifierQuotient = (a, q) => {
    const res = quotientDe(a, q);
    const attendu = valeurDe(a) / Math.pow(10, q);
    if (Math.abs(valeurDe(res) - attendu) > attendu * 1e-12 + 1e-12) {
        throw new Error(`gen_1009 : glissement faux (${brut(a)} ÷ 10^${q})`);
    }
    if (res.dec.length > 3) {
        throw new Error(`gen_1009 : quotient hors domaine (${brut(res)})`);
    }
    return res;
};

/** Entier de 1 à 4 chiffres, zéros finaux ADMIS (850, 1250 : zéro avalé). */
const tirerEntier = () => {
    const len = randomChoice([1, 1, 2, 2, 2, 3, 3, 4]);
    const a = randInt(len === 1 ? 2 : Math.pow(10, len - 1), Math.pow(10, len) - 1);
    return { ent: String(a), dec: '' };
};

/** Décimal canonique : E ∈ [0..12], D sans zéro final, 1 à 3 décimales. */
const tirerDecimal = () => {
    for (let t = 0; t < 100; t++) {
        const p = randInt(1, 3);
        const D = randInt(1, Math.pow(10, p) - 1);
        if (D % 10 === 0) continue;
        const E = randInt(0, 12);
        return { ent: String(E), dec: String(D).padStart(p, '0') };
    }
    return { ent: '3', dec: '2' };
};

/** q licites pour un opérande : dec(a) + q ≤ 3 (résultat ≤ 3 décimales). */
const qsLicites = (a) => [1, 2, 3].filter((q) => a.dec.length + q <= 3);

const M_AFFICHE = ['1', '10', '100', '1 000'];
const M_BRUT = ['1', '10', '100', '1000'];

/** item = { a, q, trou } ; trou ∈ {'res','div'}. */
const ligneItem = (item, slot = null) => {
    const { a, q, trou } = item;
    const res = verifierQuotient(a, q);
    const marque = (v) => (slot === 'trou' ? `{{${v}}}` : null);
    if (trou === 'div' && slot !== null) {
        return `${affiche(a)} ÷ ${marque(M_BRUT[q])} = ${affiche(res)}`;
    }
    if (slot !== null) {
        return `${affiche(a)} ÷ ${M_AFFICHE[q]} = ${marque(brut(res))}`;
    }
    return `${affiche(a)} ÷ ${M_AFFICHE[q]} = ${affiche(res)}`;
};

/**
 * Tire `nb` items : EXACTEMENT un « ÷ 1 », ≥ 2 entiers (≥ 1 si nb = 3),
 * ≥ 1 décimal, ≥ 1 résultat < 1, opérandes distincts (mots distincts si
 * demandé). Boucle bornée ; repli déterministe : les cas de la flash.
 */
const tirerItems = (nb, { motsDistincts = false } = {}) => {
    const motDe = (it) => (it.trou === 'div' ? M_BRUT[it.q] : brut(quotientDe(it.a, it.q)));
    const minEntiers = nb >= 4 ? 2 : 1;
    for (let essai = 0; essai < 80; essai++) {
        const pris = [];
        const vus = new Set();
        const mots = new Set();
        let avecUn = 0;
        for (let t = 0; t < 200 && pris.length < nb; t++) {
            const estEntier = Math.random() < 0.6;
            const a = estEntier ? tirerEntier() : tirerDecimal();
            const licites = qsLicites(a);
            let q;
            if (avecUn === 0 && pris.length === nb - 1) q = 0;
            else if (avecUn === 0 && Math.random() < 0.25) q = 0;
            else if (licites.length === 0) continue;
            else q = randomChoice(licites);
            const trou = q >= 1 && Math.random() < 0.25 ? 'div' : 'res';
            const it = { a, q, trou };
            if (vus.has(brut(a))) continue;
            if (motsDistincts && mots.has(motDe(it))) continue;
            vus.add(brut(a));
            mots.add(motDe(it));
            if (q === 0) avecUn++;
            pris.push(it);
        }
        const entiers = pris.filter((it) => !it.a.dec).length;
        const decimaux = pris.length - entiers;
        const sousUn = pris.filter((it) => valeurDe(quotientDe(it.a, it.q)) < 1).length;
        if (pris.length === nb
            && pris.filter((it) => it.q === 0).length === 1
            && entiers >= minEntiers && decimaux >= 1 && sousUn >= 1) return shuffle(pris);
    }
    return [
        { a: { ent: '47', dec: '' }, q: 1, trou: 'res' },      // 47 ÷ 10
        { a: { ent: '850', dec: '' }, q: 3, trou: 'res' },     // 850 ÷ 1 000
        { a: { ent: '9', dec: '4' }, q: 0, trou: 'res' },      // 9,4 ÷ 1
        { a: { ent: '3', dec: '2' }, q: 2, trou: 'div' },      // 3,2 ÷ ? = 0,032
    ].slice(0, nb);
};

// ─── Format 1 : texte à trous SAISIE (la série flash) ─────────────────────

const genQuotientsTrouSaisie = () => {
    const items = tirerItems(4);
    const phrase = items.map((it) => ligneItem(it, 'trou')).join('\n');
    return makeTexteTrou(
        phrase,
        '<p><b>Complète chaque égalité.</b></p>',
        { ...opts(L1), mode: 'saisie' },
    );
};

// ─── Format 2 : texte à trous DRAG ────────────────────────────────────────

const genQuotientsTrouDrag = () => {
    const items = tirerItems(3, { motsDistincts: true });
    const phrase = items.map((it) => ligneItem(it, 'trou')).join('\n');
    // UN piège PAR LIGNE (arbitrage prof 2026-08-20 : aucune ligne sans
    // voisin piégeux) : sens inverse (le nombre a GRANDI), glissement ± 1,
    // diviseur voisin — chaque ligne choisit dans SES candidats, filtrés
    // contre les mots, dédoublonnés en VALEUR (leçon de la revue 1008).
    const candidatsDe = (it) => {
        if (it.trou === 'div') {
            return [M_BRUT[it.q === 3 ? 2 : it.q + 1], M_BRUT[it.q === 1 ? 3 : it.q - 1]];
        }
        const c = [
            brut(canon(glisser(it.a, it.q))),                   // sens inverse
            brut(canon(glisser(it.a, it.q + 1))),               // sens inverse + 1
        ];
        if (it.a.dec.length + it.q < 3) c.push(brut(quotientDe(it.a, it.q + 1)));
        if (it.q >= 1) c.push(brut(quotientDe(it.a, it.q - 1)));
        return c;
    };
    const distracteurs = [];
    const vusVal = new Set();
    for (const it of items) {
        const ok = filtrerDistracteurs(phrase, shuffle(candidatsDe(it)))
            .find((d) => !vusVal.has(Number(d.replace(',', '.'))));
        if (ok) {
            vusVal.add(Number(ok.replace(',', '.')));
            distracteurs.push(ok);
        }
    }
    return makeTexteTrou(
        phrase,
        '<p><b>Complète chaque égalité avec les bonnes étiquettes.</b></p>',
        { ...opts(L1), mode: 'drag', distracteurs },
    );
};

// ─── Format 3 : QCM (cf. REGLES_DISTRACTEURS_QCM) ─────────────────────────

const genQuotientsQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const estEntier = Math.random() < 0.55;
        const a = estEntier ? tirerEntier() : tirerDecimal();
        const licites = qsLicites(a);
        // Un opérande sans diviseur licite (décimal à 3 décimales) est
        // RETIRÉ, pas forcé en ÷ 1 — sinon le ÷ 1 montait à 27 % au lieu
        // de ~1/7 (revue 2026-08-20).
        const veutUn = Math.random() < 0.14;
        if (!veutUn && licites.length === 0) continue;
        const q = veutUn ? 0 : randomChoice(licites);
        const res = verifierQuotient(a, q);
        const bonne = brut(res);

        let candidats;
        if (q === 0) {
            candidats = [
                { valeur: brut(quotientDe(a, 1)), code: 'routine_appliquee' },
                { valeur: brut(canon(glisser(a, 1))), code: 'routine_appliquee' },
                { valeur: brut(quotientDe(a, 2)), code: 'routine_appliquee' },
            ];
        } else {
            candidats = [
                { valeur: brut(canon(glisser(a, q))), code: 'sens_inverse' },
                q >= 2 ? { valeur: brut(quotientDe(a, q - 1)), code: 'glissement_insuffisant' }
                    : { valeur: brut(canon(glisser(a, q + 1))), code: 'sens_inverse' },
                a.dec.length + q < 3
                    ? { valeur: brut(quotientDe(a, q + 1)), code: 'glissement_excessif' }
                    : { valeur: brut(canon(glisser(a, q + 2))), code: 'sens_inverse' },
            ];
        }
        const affStr = (s) => (s.includes(',') ? s : formatNumber(Number(s)));
        const choix = [bonne, ...candidats.map((c) => c.valeur)].map(affStr);
        if (new Set(choix).size !== 4) continue;
        if (candidats.some((c) => Math.abs(
            Number(c.valeur.replace(',', '.')) - valeurDe(res),
        ) < 1e-12)) continue;

        const texte = `<p><b>Combien vaut ${affiche(a)} ÷ ${M_AFFICHE[q]} ?</b></p>`;
        const payload = makeQcm(choix, choix[0], texte, opts(L1), false);
        payload._meta.params = { a: brut(a), q, distracteurs: candidats };
        return payload;
    }
    const payload = makeQcm(
        ['4,7', '470', '0,47', '47'],
        '4,7',
        '<p><b>Combien vaut 47 ÷ 10 ?</b></p>',
        opts(L1),
        false,
    );
    payload._meta.params = { a: '47', q: 1, distracteurs: [] };
    return payload;
};

// ─── Format 4 : tableau Vrai/Faux ─────────────────────────────────────────

/** Ligne fausse = erreur NOMMÉE (sens inverse, glissement ± 1), re-vérifiée
 *  FAUSSE numériquement (throw sinon). */
const fausserItem = (item) => {
    const { a, q } = item;
    const res = quotientDe(a, q);
    const candidats = [brut(canon(glisser(a, Math.max(q, 1))))];   // sens inverse
    if (q >= 1) {
        if (a.dec.length + q < 3) candidats.push(brut(quotientDe(a, q + 1)));
        candidats.push(brut(q >= 2 ? quotientDe(a, q - 1) : canon(glisser(a, 1))));
    } else {
        candidats.push(brut(quotientDe(a, 1)));
    }
    const faux = randomChoice(candidats);
    if (Math.abs(Number(faux.replace(',', '.')) - valeurDe(res)) < 1e-12) {
        throw new Error('gen_1009 : la ligne censée être fausse est vraie');
    }
    const fauxAff = faux.includes(',') ? faux : formatNumber(Number(faux));
    return `${affiche(a)} ÷ ${M_AFFICHE[q]} = ${fauxAff}`;
};

const genQuotientsVraiFaux = () => {
    let lignes = [];
    let etiquettes = [];
    for (let essai = 0; essai < 30; essai++) {
        const items = tirerItems(4);
        const nbVraies = randInt(1, 3);
        const etiq = shuffle([
            ...Array(nbVraies).fill(0),
            ...Array(4 - nbVraies).fill(1),
        ]);
        const ls = items.map((it, i) => (
            etiq[i] === 0 ? ligneItem(it) : fausserItem(it)
        ));
        lignes = ls;
        etiquettes = etiq;
        if (new Set(ls).size === 4) break;
    }
    return makeVraiFaux(
        lignes,
        etiquettes,
        '<p><b>Vrai ou faux ?</b></p>',
        opts(L1),
    );
};

// ─── Format 5 : classer ───────────────────────────────────────────────────

/**
 * Catégories = « mêmes chiffres, glissements différents » (4,7 / 0,47 /
 * 0,047) ; items = des quotients qui les atteignent (47 ÷ 10 ; 4,7 ÷ 1 ;
 * 470 ÷ 100 ; 47 ÷ 1 000...). Le dividende (glissement GAUCHE de la cible)
 * est toujours canonique ; dividendes limités à 4 chiffres.
 */
const genQuotientsClasser = () => {
    for (let essai = 0; essai < 60; essai++) {
        const E = randInt(1, 9);
        const D = randInt(1, 9);
        const base = { ent: String(E), dec: String(D) };            // ex. 4,7
        const cibles = shuffle([0, 1, 2]).slice(0, randInt(2, 3));
        let horsDomaine = false;
        const categories = cibles.map((k) => {
            const cible = quotientDe(base, k);
            const qsValides = [0, 1, 2, 3].filter((q) => {
                const dividende = canon(glisser(cible, q));
                return dividende.ent.length <= 4
                    && cible.dec.length <= 3;
            });
            if (qsValides.length < 2) { horsDomaine = true; return null; }
            const qs = shuffle(qsValides).slice(0, 2);
            const items = qs.map((q) => {
                const dividende = canon(glisser(cible, q));
                verifierQuotient(dividende, q);
                return `${affiche(dividende)} ÷ ${M_AFFICHE[q]}`;
            });
            return { name: affiche(cible), items };
        });
        if (horsDomaine) continue;
        const tous = categories.flatMap((c) => c.items);
        if (new Set(tous).size !== tous.length) continue;
        return makeClasser(
            categories,
            '<p><b>Classe chaque quotient selon son résultat.</b></p>',
            opts(L1),
        );
    }
    return makeClasser(
        [
            { name: '4,7', items: ['47 ÷ 10', '4,7 ÷ 1'] },
            { name: '0,47', items: ['47 ÷ 100', '4,7 ÷ 10'] },
            { name: '0,047', items: ['47 ÷ 1 000', '0,47 ÷ 10'] },
        ],
        '<p><b>Classe chaque quotient selon son résultat.</b></p>',
        opts(L1),
    );
};

// ─── Contrat distracteurs (référence de revue, format QCM) ────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'chaque distracteur est une erreur NOMMÉE, textuellement distincte (garde Set) ET de valeur différente de la clé (garde numérique, re-tirage sinon)',
        'le calcul est un GLISSEMENT DE CHAÎNE vers la droite, résultat CANONIQUE (zéros finaux avalés : 850 ÷ 1 000 = 0,85), jamais un flottant',
    ],
    quotients_qcm_unique: {
        support: 'un quotient a ÷ m (a entier ~majoritaire, zéros finaux admis ; m ∈ {1, 10, 100, 1000})',
        fixes: {
            sens_inverse: 'LA confusion : le nombre grandit au lieu de rapetisser (47 ÷ 10 = 470)',
            glissement_insuffisant: 'un rang de moins que nécessaire (3,2 ÷ 100 = 0,32)',
            glissement_excessif: 'un rang de trop (47 ÷ 10 = 0,47)',
            routine_appliquee: '÷ 1 : l\'élève glisse quand même (9,4 ÷ 1 = 0,94 ou 94)',
        },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    quotients_texte_trou_saisie: genQuotientsTrouSaisie,
    quotients_texte_trou_drag: genQuotientsTrouDrag,
    quotients_qcm_unique: genQuotientsQcm,
    quotients_tableau_vf: genQuotientsVraiFaux,
    quotients_classer: genQuotientsClasser,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    tableau_vf: 'Vrai/Faux', classer: 'Classer',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'quotients', label: L1, formatsList: buildFormats('quotients') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
